import type { Action, FieldMapping, MapArrayAction } from '../OutputMapperEditor/types.ts';

/**
 * A template variable offered in the Template tab: `value` is the mapping path (`positions[].qty`), `insert` the
 * text placed into the template, `kind` tells a plain value from an array that is inserted as a loop.
 */
export interface TemplateVariable {
    value: string;
    insert: string;
    kind: 'variable' | 'loop' | 'builtin';
}

/** Values every template and file name can use besides the target fields (backend `template_builtins`). */
export const BUILTIN_TEMPLATE_VARIABLES: TemplateVariable[] = [
    'workist.record_id',
    'workist.assisted_by_email',
    'workist.created_by_email',
    'workist.message_id',
    'workist.short_id',
    'workist.sequence_number',
    'workist.date',
    'workist.time',
    "workist.now.strftime('%d.%m.%Y')",
].map((value) => ({ value, insert: `{{ ${value} }}`, kind: 'builtin' as const }));

const collectMapArrayActions = (actions: Action[] | undefined): MapArrayAction[] =>
    (actions ?? []).flatMap((action) => {
        if (action.action_type === 'map_array') return [action];
        if (action.action_type === 'conditional')
            return [...collectMapArrayActions(action.true_actions), ...collectMapArrayActions(action.false_actions)];
        return [];
    });

const loopVariable = (depth: number) => (depth <= 1 ? 'item' : `item${depth}`);

/**
 * Flatten the output mapping into template variables, descending into `map_array` field mappings: an array target
 * is offered as a `{% for %}` skeleton and its nested targets as loop-variable fields (`positions[].qty` inserts
 * `{{ item.qty }}`). Loop variables are named `item`, `item2`, … per nesting level, matching the generated, the
 * AI-written and the starter templates.
 */
export const collectTemplateVariables = (
    mappings: FieldMapping[],
    pathPrefix = '',
    loopDepth = 0
): TemplateVariable[] => {
    const scope = loopDepth === 0 ? '' : `${loopVariable(loopDepth)}.`;
    return mappings.flatMap((mapping) => {
        const path = pathPrefix + mapping.target;
        const arrayActions = collectMapArrayActions(mapping.actions);
        if (arrayActions.length === 0)
            return [{ value: path, insert: `{{ ${scope}${mapping.target} }}`, kind: 'variable' as const }];
        return [
            {
                value: `${path}[]`,
                insert: `{% for ${loopVariable(loopDepth + 1)} in ${scope}${mapping.target} %}\n\n{% endfor %}`,
                kind: 'loop' as const,
            },
            ...arrayActions.flatMap((action) =>
                collectTemplateVariables(action.field_mappings, `${path}[].`, loopDepth + 1)
            ),
        ];
    });
};

/** A target field the AI proposed: `target` is `name` or `collection[].name`, `source` a listed input path. */
export interface NewTargetField {
    target: string;
    source: string;
    type: string;
}

/**
 * Add AI-proposed target fields to the mapping. A header field becomes an `extract_source` mapping; a line-item field
 * (`collection[].name` from `Type.array[].field`) goes into the collection's `map_array`, read as `@item.field` —
 * the collection mapping is created over the source array when it doesn't exist yet.
 */
export const addTargetFields = (mappings: FieldMapping[], fields: NewTargetField[]): FieldMapping[] => {
    const next = mappings.map((mapping) => ({ ...mapping }));
    fields.forEach(({ target, source }) => {
        const [collection, name] = target.split('[].');
        if (!name) {
            next.push({
                target,
                actions: [{ action_type: 'extract_source', source }],
                is_required: false,
            } as FieldMapping);
            return;
        }
        const [arraySource, itemPath] = source.split('[].');
        const child = {
            target: name,
            actions: [{ action_type: 'extract_source', source: `@item.${itemPath}` }],
            is_required: false,
        } as FieldMapping;
        const index = next.findIndex((mapping) => mapping.target === collection);
        const mapArray = index >= 0 ? collectMapArrayActions(next[index].actions)[0] : undefined;
        if (index >= 0 && mapArray) {
            next[index] = {
                ...next[index],
                actions: next[index].actions.map((action) =>
                    action === mapArray ? { ...mapArray, field_mappings: [...mapArray.field_mappings, child] } : action
                ),
            };
            return;
        }
        next.push({
            target: collection,
            actions: [{ action_type: 'map_array', source: arraySource, item_alias: 'item', field_mappings: [child] }],
            is_required: false,
        } as FieldMapping);
    });
    return next;
};

/** The input list each top-level array target loops over (`{ line_items: 'Order.line_items' }`). */
export const collectCollectionSources = (mappings: FieldMapping[]): Record<string, string> =>
    Object.fromEntries(
        mappings.flatMap((mapping) => {
            const mapArray = collectMapArrayActions(mapping.actions)[0];
            return mapArray ? [[mapping.target, mapArray.source]] : [];
        })
    );
